import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  X,
  MoreHorizontal,
  Pencil,
  Trash2,
  Database,
  Check,
  XCircle,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PageHeader, MetricCard } from '@/components/common';
import {
  chargeTypesApi,
  ChargeType,
  ChargeCategoryExtended,
  RateUnit,
  CreateChargeTypeDto,
  QueryChargeTypeParams,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';

// Constants
const CHARGE_CATEGORIES: { value: ChargeCategoryExtended; label: string }[] = [
  { value: 'Freight', label: 'Freight' },
  { value: 'OriginCharges', label: 'Origin Charges' },
  { value: 'DestinationCharges', label: 'Destination Charges' },
  { value: 'OriginHaulage', label: 'Origin Haulage' },
  { value: 'DestinationHaulage', label: 'Destination Haulage' },
  { value: 'ExportClearance', label: 'Export Clearance' },
  { value: 'ImportClearance', label: 'Import Clearance' },
  { value: 'Documentation', label: 'Documentation' },
  { value: 'Insurance', label: 'Insurance' },
];

const RATE_UNITS: { value: RateUnit; label: string }[] = [
  { value: 'PerContainer', label: 'Per Container' },
  { value: 'PerCBM', label: 'Per CBM' },
  { value: 'PerKG', label: 'Per KG' },
  { value: 'PerMile', label: 'Per Mile' },
  { value: 'PerCWT', label: 'Per CWT' },
  { value: 'PerTon', label: 'Per Ton' },
  { value: 'PerPallet', label: 'Per Pallet' },
  { value: 'PerUnit', label: 'Per Unit' },
  { value: 'PerShipment', label: 'Per Shipment' },
  { value: 'Flat', label: 'Flat' },
  { value: 'Percentage', label: 'Percentage' },
];

const CATEGORY_COLORS: Record<ChargeCategoryExtended, string> = {
  Freight: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  OriginCharges: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  DestinationCharges: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  OriginHaulage: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  DestinationHaulage: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  ExportClearance: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  ImportClearance: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  Documentation: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  Insurance: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
};

export default function ChargeTypes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [chargeTypes, setChargeTypes] = useState<ChargeType[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  // Sheet/Dialog states
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingChargeType, setEditingChargeType] = useState<ChargeType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chargeToDelete, setChargeToDelete] = useState<ChargeType | null>(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateChargeTypeDto>({
    code: '',
    name: '',
    category: 'Freight',
    description: '',
    rateUnit: 'Flat',
    isForRFQ: true,
  });

  // Parse filters from URL
  const filters: QueryChargeTypeParams = {
    search: searchParams.get('search') || undefined,
    category: (searchParams.get('category') as ChargeCategoryExtended) || undefined,
    isActive: searchParams.get('isActive') === 'true' ? true : searchParams.get('isActive') === 'false' ? false : undefined,
    page: parseInt(searchParams.get('page') || '1'),
    limit: parseInt(searchParams.get('limit') || '50'),
  };

  // Set filter helper
  const setFilter = (key: keyof QueryChargeTypeParams, value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    if (key !== 'page') {
      params.set('page', '1');
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = filters.search || filters.category || filters.isActive !== undefined;

  // Load data
  const loadChargeTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await chargeTypesApi.getAll(filters);
      setChargeTypes(response.data);
      setTotalCount(response.meta?.total ?? response.data.length);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    loadChargeTypes();
  }, [loadChargeTypes]);

  // Computed stats
  const activeCount = useMemo(() => chargeTypes.filter((c) => c.isActive).length, [chargeTypes]);
  const rfqEnabledCount = useMemo(() => chargeTypes.filter((c) => c.isForRFQ).length, [chargeTypes]);
  const categoryBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    chargeTypes.forEach((c) => {
      counts[c.category] = (counts[c.category] || 0) + 1;
    });
    return counts;
  }, [chargeTypes]);

  // Handlers
  const handleCreate = () => {
    setEditingChargeType(null);
    setFormData({
      code: '',
      name: '',
      category: 'Freight',
      description: '',
      rateUnit: 'Flat',
      isForRFQ: true,
    });
    setSheetOpen(true);
  };

  const handleEdit = (chargeType: ChargeType) => {
    setEditingChargeType(chargeType);
    setFormData({
      code: chargeType.code,
      name: chargeType.name,
      category: chargeType.category,
      description: chargeType.description || '',
      rateUnit: chargeType.rateUnit,
      isForRFQ: chargeType.isForRFQ,
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingChargeType) {
        await chargeTypesApi.update(editingChargeType.id, formData);
        toast.success('Charge type updated successfully');
      } else {
        await chargeTypesApi.create(formData);
        toast.success('Charge type created successfully');
      }
      setSheetOpen(false);
      await loadChargeTypes();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!chargeToDelete) return;

    try {
      await chargeTypesApi.delete(chargeToDelete.id);
      toast.success('Charge type deleted successfully');
      setDeleteDialogOpen(false);
      setChargeToDelete(null);
      await loadChargeTypes();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleActive = async (chargeType: ChargeType) => {
    try {
      await chargeTypesApi.toggleActive(chargeType.id);
      toast.success(`Charge type ${chargeType.isActive ? 'deactivated' : 'activated'}`);
      await loadChargeTypes();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const result = await chargeTypesApi.seedDefaults();
      toast.success(`${result.created} default charge types created`);
      await loadChargeTypes();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Charge Types"
        description="Manage charge types for rates and RFQ submissions"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSeedDefaults} disabled={seeding}>
              <Database className="w-4 h-4 mr-2" />
              {seeding ? 'Seeding...' : 'Seed Defaults'}
            </Button>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              New Charge Type
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total Charge Types"
          value={totalCount}
          icon={<FileText className="w-5 h-5" />}
        />
        <MetricCard
          label="Active"
          value={activeCount}
          icon={<Check className="w-5 h-5 text-emerald-500" />}
        />
        <MetricCard
          label="RFQ Enabled"
          value={rfqEnabledCount}
          icon={<FileText className="w-5 h-5 text-blue-500" />}
        />
        <MetricCard
          label="Categories"
          value={Object.keys(categoryBreakdown).length}
          icon={<Database className="w-5 h-5 text-purple-500" />}
        />
      </div>

      {/* Filters */}
      <div className="bg-card rounded-xl border p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by code or name..."
              value={filters.search || ''}
              onChange={(e) => setFilter('search', e.target.value || undefined)}
              className="pl-10 pr-10"
            />
            {filters.search && (
              <button
                onClick={() => setFilter('search', undefined)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Category Filter */}
          <Select
            value={filters.category || ''}
            onValueChange={(v) => setFilter('category', v || undefined)}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All Categories</SelectItem>
              {CHARGE_CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Active Filter */}
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={filters.isActive === true}
              onCheckedChange={(checked) =>
                setFilter('isActive', checked ? 'true' : undefined)
              }
            />
            Active Only
          </label>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-destructive hover:text-destructive"
            >
              <X className="w-4 h-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Code
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Category
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Rate Unit
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  RFQ
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-10">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </td>
                </tr>
              ) : chargeTypes.length > 0 ? (
                chargeTypes.map((chargeType) => (
                  <tr key={chargeType.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium">{chargeType.code}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium">{chargeType.name}</div>
                        {chargeType.description && (
                          <div className="text-sm text-muted-foreground truncate max-w-xs">
                            {chargeType.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={CATEGORY_COLORS[chargeType.category]}>
                        {chargeType.category.replace(/([A-Z])/g, ' $1').trim()}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {chargeType.rateUnit
                        ? RATE_UNITS.find((u) => u.value === chargeType.rateUnit)?.label || chargeType.rateUnit
                        : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {chargeType.isActive ? (
                        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          Active
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400">
                          Inactive
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {chargeType.isForRFQ ? (
                        <Check className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-muted-foreground" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(chargeType)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleActive(chargeType)}>
                            {chargeType.isActive ? (
                              <>
                                <XCircle className="w-4 h-4 mr-2" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                Activate
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setChargeToDelete(chargeType);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <AlertTriangle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Charge Types Found</h3>
                    <p className="text-muted-foreground mb-4">
                      {hasActiveFilters
                        ? 'No charge types match your filters.'
                        : 'Get started by creating a charge type or seeding defaults.'}
                    </p>
                    {!hasActiveFilters && (
                      <div className="flex items-center justify-center gap-2">
                        <Button variant="outline" onClick={handleSeedDefaults}>
                          <Database className="w-4 h-4 mr-2" />
                          Seed Defaults
                        </Button>
                        <Button onClick={handleCreate}>
                          <Plus className="w-4 h-4 mr-2" />
                          Create First
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              {editingChargeType ? 'Edit Charge Type' : 'New Charge Type'}
            </SheetTitle>
            <SheetDescription>
              {editingChargeType
                ? 'Update the charge type details below.'
                : 'Create a new charge type for rate management.'}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 py-6">
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value.toUpperCase() })
                }
                placeholder="e.g., THC, BAF, DOC"
                disabled={!!editingChargeType}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Terminal Handling Charge"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(v) =>
                  setFormData({ ...formData, category: v as ChargeCategoryExtended })
                }
              >
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHARGE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rateUnit">Rate Unit</Label>
              <Select
                value={formData.rateUnit || ''}
                onValueChange={(v) =>
                  setFormData({ ...formData, rateUnit: v as RateUnit })
                }
              >
                <SelectTrigger id="rateUnit">
                  <SelectValue placeholder="Select unit..." />
                </SelectTrigger>
                <SelectContent>
                  {RATE_UNITS.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="isForRFQ"
                checked={formData.isForRFQ}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isForRFQ: !!checked })
                }
              />
              <Label htmlFor="isForRFQ" className="font-normal">
                Available for RFQ submissions
              </Label>
            </div>
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingChargeType ? 'Update' : 'Create'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              Delete Charge Type
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium">{chargeToDelete?.code}</span>? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
